/**
 * TextInput — React (web) implementation.
 *
 * Floating in-field label: the label sits centered inside the field as a
 * placeholder while empty and unfocused, then minimizes to a caption at the top
 * of the field with the member's value below once focused or filled.
 * All styling references the shared constants in TextInput.ts.
 */

import React, { useState } from 'react';
import { preventOrphan } from '@galapagos/shared';
import { Icon } from '../Icon/Icon.web';
import { colors } from '../../elements/colors';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import {
  FIELD_ERROR_GAP,
  FIELD_ERROR_ICON,
  FIELD_ERROR_ICON_COLOR,
  FIELD_ERROR_ICON_SIZE,
  FIELD_ERROR_TEXT_COLOR,
  FIELD_ERROR_TYPOGRAPHY,
} from '../FieldError/FieldError';
import {
  textInputColorsFor,
  textInputAccessibleLabel,
  isTextInputMinimized,
  TEXT_INPUT_BORDER_RADIUS,
  TEXT_INPUT_BORDER_WIDTH,
  TEXT_INPUT_HORIZONTAL_PADDING,
  TEXT_INPUT_VERTICAL_PADDING,
  TEXT_INPUT_HEIGHT,
  TEXT_INPUT_MULTILINE_DEFAULT_LINES,
  TEXT_INPUT_LABEL_VALUE_GAP,
  TEXT_INPUT_HELPER_GAP,
  TEXT_INPUT_MINIMIZED_LABEL_TYPOGRAPHY,
  TEXT_INPUT_PLACEHOLDER_TYPOGRAPHY,
  TEXT_INPUT_VALUE_TYPOGRAPHY,
  TEXT_INPUT_HELPER_TYPOGRAPHY,
} from './TextInput';

// Web draws an enabled, error-free field's border in the field-border stroke, so
// the field edge meets the 3:1 non-text contrast minimum on white. The shared
// colours keep the lighter divider because native reads them too; disabled and
// error fields keep the shared border.
const WEB_TEXT_INPUT_RESTING_BORDER = colors.stroke.input;

// Web draws the label in the secondary text colour whether it rests in the field
// or floats above the value, as PhoneInput and Select do, so the field's only
// visible name stays readable on white. Native keeps the shared label colours.
const WEB_TEXT_INPUT_LABEL = colors.text.secondary;

const TEXT_INPUT_FOCUS_STYLE_ID = 'galapagos-text-input-focus-visible';
const TEXT_INPUT_FIELD_SELECTOR = '[data-galapagos-text-input-field="true"]';

// The focusable <input> is transparent (opacity 0) while the field is empty and
// unfocused, so a :focus-visible outline on the input itself could be invisible.
// Draw the ADR-025 ring on the field container instead, gated on a descendant
// matching :focus-visible so the ring shows for keyboard focus only (never a
// pointer tap), which is the accessibility behaviour :focus-visible exists to
// provide.
function ensureTextInputFocusStyle(): void {
  ensureStyle(
    TEXT_INPUT_FOCUS_STYLE_ID,
    `${TEXT_INPUT_FIELD_SELECTOR}:has(:focus-visible) { ${FOCUS_RING_OUTLINE} }`,
  );
}

const TEXT_INPUT_INTERACTION_STYLE_ID = 'galapagos-text-input-interaction';
const TEXT_INPUT_INTERACTIVE_FIELD_SELECTOR = `${TEXT_INPUT_FIELD_SELECTOR}:not([data-disabled="true"]):not([data-error="true"])`;

// Web hover and focus darken the field border to the focused border the shared
// colours already define (native draws it on focus). Done in CSS, so a pointer
// hover needs no component state; !important lets the rule beat the field's
// inline border. Disabled and error fields are excluded, so their borders
// always win. Hover only applies to devices with a real hover (a tap must not
// leave the border dark), and the focus border sits in its own `:has()` rule so
// a browser without `:has()` still gets hover.
function ensureTextInputInteractionStyle(): void {
  const focusedBorder = textInputColorsFor(false, false, true).border;
  ensureStyle(
    TEXT_INPUT_INTERACTION_STYLE_ID,
    [
      `@media (hover: hover) { ${TEXT_INPUT_INTERACTIVE_FIELD_SELECTOR}:hover { border-color: ${focusedBorder} !important; } }`,
      `@supports selector(:has(*)) { ${TEXT_INPUT_INTERACTIVE_FIELD_SELECTOR}:has(:focus) { border-color: ${focusedBorder} !important; } }`,
    ].join('\n'),
  );
}

// Visually hidden but announced: a live region must already be mounted when its
// text changes, so this one is always rendered.
const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
};

export interface WebTextInputProps {
  label: string;
  value?: string;
  defaultValue?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
  disabled?: boolean;
  /** Placeholder-only field: suppress the floating label (the placeholder +
   *  aria-label carry the field name instead), matching the native platform. */
  hideLabel?: boolean;
  /** Overrides the hidden-label placeholder text; falls back to the label
   *  itself when omitted. Ignored when `hideLabel` is false. */
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  /** Form-field `name` for browser and password-manager autofill heuristics;
   *  defaults to the derived input id so every field carries one. */
  name?: string;
  /** Semantic input type for a single-line field (`email`, `tel`, …) so the
   *  browser applies its own validation hints and mobile keyboards; ignored
   *  when `multiline`. Defaults to `text`. */
  type?: 'text' | 'email' | 'tel' | 'url' | 'search';
  /** Virtual-keyboard hint, independent of `type` (e.g. `numeric` for a masked
   *  date field that stays `type="text"`). */
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** Multi-line entry (e.g. a note); grows to `numberOfLines` visible lines. */
  multiline?: boolean;
  numberOfLines?: number;
  onChangeText?: (text: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  /** ARIA combobox wiring for a suggestion-driven consumer (e.g. Autocomplete),
   *  per the WAI-ARIA combobox pattern. Applied to the single-line input only —
   *  a multiline field is never a combobox. */
  combobox?: {
    expanded: boolean;
    /** id of the controlled listbox element. */
    controls: string;
    /** id of the visually-highlighted option, when any. */
    activeDescendant?: string;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  };
  'data-testid'?: string;
  'aria-label'?: string;
  /** Domain action id for an edit of this field (ADR-081 Rule 4). */
  actionId?: string;
}

const VALUE_LINE_TOP =
  TEXT_INPUT_VERTICAL_PADDING +
  TEXT_INPUT_MINIMIZED_LABEL_TYPOGRAPHY.lineHeight +
  TEXT_INPUT_LABEL_VALUE_GAP;

export function TextInput({
  label,
  value,
  defaultValue,
  helperText,
  errorText,
  required = false,
  disabled = false,
  hideLabel = false,
  placeholder,
  autoFocus = false,
  autoComplete,
  name,
  type = 'text',
  inputMode,
  multiline = false,
  numberOfLines,
  onChangeText,
  onBlur,
  onFocus,
  combobox,
  'data-testid': testId,
  'aria-label': ariaLabel,
  actionId = 'ui.text-input.change',
}: WebTextInputProps) {
  ensureTextInputFocusStyle();
  ensureTextInputInteractionStyle();

  // An uncontrolled field remembers what was typed, so its label stays floated
  // after the member moves on.
  const [ownValue, setOwnValue] = useState(defaultValue ?? '');
  const [focused, setFocused] = useState(false);
  const currentValue = value ?? ownValue;

  const hasError = !!errorText;
  // Focus floats the label too, which reveals the input and its caret.
  const minimized = isTextInputMinimized(currentValue, focused && !disabled);
  const inputColors = textInputColorsFor(disabled, hasError);
  const fieldBorder = disabled || hasError ? inputColors.border : WEB_TEXT_INPUT_RESTING_BORDER;

  const inputId = testId
    ? `${testId}-input`
    : `textinput-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const helperId = (errorText ?? helperText) ? `${inputId}-helper` : undefined;
  const inputName = name ?? inputId;

  // The real control is a thin strip inside a 70px box (and transparent while
  // empty), so a click on most of the box — including the pointer-events-none
  // floating label — would otherwise land on the container and do nothing.
  // Hand it to the control, the way OtpInput's cell overlay does. Only
  // `focus()`, never `preventDefault`: a press on the control itself keeps its
  // native caret placement and text selection.
  const focusFieldInput: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (disabled) return;
    event.currentTarget
      .querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
      ?.focus();
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: TEXT_INPUT_HELPER_GAP,
  };

  // Multiline parity with native: the field grows to its visible line count
  // instead of the fixed single-line height.
  const visibleLines = numberOfLines ?? TEXT_INPUT_MULTILINE_DEFAULT_LINES;
  const multilineFieldHeight =
    VALUE_LINE_TOP +
    visibleLines * TEXT_INPUT_VALUE_TYPOGRAPHY.lineHeight +
    TEXT_INPUT_VERTICAL_PADDING;

  const fieldStyle: React.CSSProperties = {
    position: 'relative',
    height: multiline ? multilineFieldHeight : TEXT_INPUT_HEIGHT,
    boxSizing: 'border-box',
    backgroundColor: inputColors.background,
    border: `${TEXT_INPUT_BORDER_WIDTH}px solid ${fieldBorder}`,
    borderRadius: TEXT_INPUT_BORDER_RADIUS,
    paddingLeft: TEXT_INPUT_HORIZONTAL_PADDING,
    paddingRight: TEXT_INPUT_HORIZONTAL_PADDING,
    cursor: disabled ? 'not-allowed' : 'text',
  };

  const labelTypography = minimized
    ? TEXT_INPUT_MINIMIZED_LABEL_TYPOGRAPHY
    : TEXT_INPUT_PLACEHOLDER_TYPOGRAPHY;
  const labelStyle: React.CSSProperties = {
    position: 'absolute',
    left: TEXT_INPUT_HORIZONTAL_PADDING,
    right: TEXT_INPUT_HORIZONTAL_PADDING,
    pointerEvents: 'none',
    fontFamily: labelTypography.fontFamily,
    fontWeight: labelTypography.fontWeight,
    fontSize: labelTypography.fontSize,
    lineHeight: `${labelTypography.lineHeight}px`,
    letterSpacing: labelTypography.letterSpacing,
    color: disabled ? inputColors.label : WEB_TEXT_INPUT_LABEL,
    ...(minimized
      ? { top: TEXT_INPUT_VERTICAL_PADDING }
      : { top: '50%', transform: 'translateY(-50%)' }),
  };

  const inputStyle: React.CSSProperties = {
    position: 'absolute',
    left: TEXT_INPUT_HORIZONTAL_PADDING,
    right: TEXT_INPUT_HORIZONTAL_PADDING,
    // A `hideLabel` field has no floating-label overlay to ever hand off to —
    // there's nothing else occupying the field, so it stays centered like a
    // plain placeholder-only input (matching native's `valuePlain`, which
    // never gets the floating label's marginTop) rather than sitting at the
    // "value sits below the minimized label" offset.
    ...(hideLabel ? { top: '50%', transform: 'translateY(-50%)' } : { top: VALUE_LINE_TOP }),
    margin: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: TEXT_INPUT_VALUE_TYPOGRAPHY.fontFamily,
    fontWeight: TEXT_INPUT_VALUE_TYPOGRAPHY.fontWeight,
    fontSize: TEXT_INPUT_VALUE_TYPOGRAPHY.fontSize,
    lineHeight: `${TEXT_INPUT_VALUE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: TEXT_INPUT_VALUE_TYPOGRAPHY.letterSpacing,
    color: inputColors.value,
    // Hidden until the label floats up, so an empty unfocused field reads as a
    // single centered placeholder rather than an empty line under a caption.
    // `hideLabel` has no such overlay to protect, so the input (and its own
    // native placeholder attribute) is always visible.
    opacity: hideLabel || minimized ? 1 : 0,
  };

  const helperStyle: React.CSSProperties = {
    fontFamily: TEXT_INPUT_HELPER_TYPOGRAPHY.fontFamily,
    fontWeight: TEXT_INPUT_HELPER_TYPOGRAPHY.fontWeight,
    fontSize: TEXT_INPUT_HELPER_TYPOGRAPHY.fontSize,
    lineHeight: `${TEXT_INPUT_HELPER_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: TEXT_INPUT_HELPER_TYPOGRAPHY.letterSpacing,
    color: inputColors.helper,
    paddingLeft: TEXT_INPUT_HORIZONTAL_PADDING,
  };

  // The error row matches FieldError (PhoneInput, Select, CheckboxField) so every
  // web field reports a problem the same way.
  const errorRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: FIELD_ERROR_GAP,
    paddingLeft: TEXT_INPUT_HORIZONTAL_PADDING,
  };

  const errorMessageStyle: React.CSSProperties = {
    fontFamily: FIELD_ERROR_TYPOGRAPHY.fontFamily,
    fontWeight: FIELD_ERROR_TYPOGRAPHY.fontWeight,
    fontSize: FIELD_ERROR_TYPOGRAPHY.fontSize,
    lineHeight: `${FIELD_ERROR_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: FIELD_ERROR_TYPOGRAPHY.letterSpacing,
    color: FIELD_ERROR_TEXT_COLOR,
  };

  const handleChange = (text: string) => {
    if (value === undefined) setOwnValue(text);
    onChangeText?.(text);
  };
  const handleFocus = () => {
    setFocused(true);
    onFocus?.();
  };
  const handleBlur = () => {
    setFocused(false);
    onBlur?.();
  };

  const displayLabel = required ? `${label} *` : label;

  return (
    <div style={containerStyle} data-testid={testId ? `${testId}-container` : undefined}>
      {/* analytics-exempt: forwarded-wrapper */}
      <div
        style={fieldStyle}
        data-galapagos-text-input-field="true"
        data-disabled={disabled || undefined}
        data-error={hasError || undefined}
        onClick={focusFieldInput}
      >
        {hideLabel ? null : (
          <span
            style={labelStyle}
            aria-hidden="true"
            data-testid={testId ? `${testId}-label` : undefined}
          >
            {displayLabel}
          </span>
        )}
        {multiline ? (
          <textarea
            data-analytics-action={actionId}
            id={inputId}
            name={inputName}
            rows={visibleLines}
            {...(hideLabel ? { placeholder: placeholder ?? displayLabel } : {})}
            {...(value !== undefined ? { value } : {})}
            {...(defaultValue !== undefined ? { defaultValue } : {})}
            disabled={disabled}
            autoFocus={autoFocus}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            onFocus={handleFocus}
            style={{
              ...inputStyle,
              height: visibleLines * TEXT_INPUT_VALUE_TYPOGRAPHY.lineHeight,
              resize: 'none',
            }}
            aria-label={textInputAccessibleLabel(label, ariaLabel)}
            aria-invalid={hasError}
            aria-disabled={disabled}
            aria-describedby={helperId}
            data-testid={testId}
          />
        ) : (
          <input
            data-analytics-action={actionId}
            id={inputId}
            name={inputName}
            type={type}
            inputMode={inputMode}
            {...(hideLabel ? { placeholder: placeholder ?? displayLabel } : {})}
            {...(value !== undefined ? { value } : {})}
            {...(defaultValue !== undefined ? { defaultValue } : {})}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            onFocus={handleFocus}
            style={inputStyle}
            {...(combobox
              ? {
                  role: 'combobox' as const,
                  'aria-expanded': combobox.expanded,
                  'aria-controls': combobox.controls,
                  'aria-autocomplete': 'list' as const,
                  'aria-activedescendant': combobox.activeDescendant,
                  onKeyDown: combobox.onKeyDown,
                }
              : {})}
            aria-label={textInputAccessibleLabel(label, ariaLabel)}
            aria-invalid={hasError}
            aria-disabled={disabled}
            aria-describedby={helperId}
            data-testid={testId}
          />
        )}
      </div>
      {hasError ? (
        // Hidden from browsing (the live region below already reads it out);
        // still the input's description through aria-describedby.
        <div style={errorRowStyle} id={helperId} aria-hidden>
          <Icon
            name={FIELD_ERROR_ICON}
            size={FIELD_ERROR_ICON_SIZE}
            color={FIELD_ERROR_ICON_COLOR}
            aria-hidden
          />
          <span style={errorMessageStyle} data-testid={testId ? `${testId}-error` : undefined}>
            {preventOrphan(errorText)}
          </span>
        </div>
      ) : helperText ? (
        <span id={helperId} style={helperStyle}>
          {preventOrphan(helperText)}
        </span>
      ) : null}
      <span role="status" aria-live="polite" style={VISUALLY_HIDDEN_STYLE} data-ph-no-capture>
        {errorText}
      </span>
    </div>
  );
}
